// Edit profile bottom sheet — displayName + username editor.
//
// Validation mirrors apps/api/src/domain/users/profile.ts so the Save button
// can disable without a round-trip. Server is still the source of truth for
// uniqueness + reserved-word checks.

import { useEffect, useState } from 'react'
import { type CurrentUser, meApi } from '../api'

// Mirror of apps/api/src/domain/users/profile.ts constants.
const USERNAME_RE = /^[a-z0-9_]{3,20}$/
const DISPLAY_MIN = 1
const DISPLAY_MAX = 50
const USERNAME_MIN = 3
const USERNAME_MAX = 20

function emailLocalPart(email: string | null | undefined): string {
  if (!email) return ''
  const at = email.indexOf('@')
  return at > 0 ? email.slice(0, at) : email
}

function sanitizeUsernameDefault(raw: string): string {
  // Strip anything not allowed; cap at max length.
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '_')
    .slice(0, USERNAME_MAX)
}

export type EditProfileSheetProps = {
  user: CurrentUser
  onCancel: () => void
  onSaved: (u: CurrentUser) => void
}

export function EditProfileSheet({ user, onCancel, onSaved }: EditProfileSheetProps) {
  const defaultDisplay = user.displayName || emailLocalPart(user.email) || ''
  const defaultUsername =
    user.username ?? sanitizeUsernameDefault(emailLocalPart(user.email) || 'user')

  const [displayName, setDisplayName] = useState(defaultDisplay)
  const [username, setUsername] = useState(defaultUsername)
  const [saving, setSaving] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)

  // Escape key cancels.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel])

  const displayTrim = displayName.trim()
  const usernameLower = username.trim().toLowerCase()

  const displayError =
    displayTrim.length < DISPLAY_MIN || displayTrim.length > DISPLAY_MAX
      ? `${DISPLAY_MIN}-${DISPLAY_MAX} chars`
      : null
  const usernameError = !USERNAME_RE.test(usernameLower)
    ? `${USERNAME_MIN}-${USERNAME_MAX} chars, a-z 0-9 _`
    : null

  const displayChanged = displayTrim !== (user.displayName ?? '').trim()
  const usernameChanged = usernameLower !== (user.username ?? '').toLowerCase()
  const hasChanges = displayChanged || usernameChanged
  const canSave = hasChanges && !displayError && !usernameError && !saving

  async function onSave() {
    if (!canSave) return
    setSaving(true)
    setServerError(null)
    const patch: { displayName?: string; username?: string } = {}
    if (displayChanged) patch.displayName = displayTrim
    if (usernameChanged) patch.username = usernameLower
    try {
      const res = await meApi.update(patch)
      if (res.ok) {
        onSaved(res.user)
        return
      }
      if (res.reason === 'username_taken') setServerError('username already taken')
      else if (res.reason === 'username_reserved') setServerError('username is reserved')
      else if (res.reason === 'invalid_username') setServerError('invalid username')
      else if (res.reason === 'invalid_display_name') setServerError('invalid display name')
    } catch {
      setServerError("couldn't save — try again")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="edit-profile-backdrop" data-testid="edit-profile-sheet">
      <button
        type="button"
        className="edit-profile-dismiss"
        aria-label="Cancel edit profile"
        onClick={onCancel}
        disabled={saving}
      />
      <dialog
        open
        aria-modal="true"
        aria-labelledby="edit-profile-title"
        className="edit-profile-sheet"
      >
        <div className="eyebrow" id="edit-profile-title" style={{ marginBottom: 6 }}>
          edit profile
        </div>
        <div className="display" style={{ fontSize: 26, lineHeight: 0.95, marginBottom: 14 }}>
          who are you, really?
        </div>

        <label className="eyebrow" htmlFor="edit-display-name" style={{ display: 'block' }}>
          display name
        </label>
        <input
          id="edit-display-name"
          data-testid="edit-display-name"
          type="text"
          value={displayName}
          onChange={(e) => {
            setDisplayName(e.target.value)
            setServerError(null)
          }}
          maxLength={DISPLAY_MAX + 10}
          autoComplete="off"
          autoCapitalize="words"
          disabled={saving}
          className="edit-profile-input"
        />
        {displayError && displayTrim.length > 0 && (
          <div className="edit-profile-error" data-testid="edit-display-error">
            {displayError}
          </div>
        )}

        <label
          className="eyebrow"
          htmlFor="edit-username"
          style={{ display: 'block', marginTop: 12 }}
        >
          username
        </label>
        <input
          id="edit-username"
          data-testid="edit-username"
          type="text"
          value={username}
          onChange={(e) => {
            // Lowercase as typed so the preview matches what we send.
            setUsername(e.target.value.toLowerCase())
            setServerError(null)
          }}
          maxLength={USERNAME_MAX + 4}
          autoComplete="off"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          disabled={saving}
          className="edit-profile-input"
        />
        <div className="body-sm muted" style={{ fontSize: 12, marginTop: 4 }}>
          stmbl.party/u/{usernameLower || 'username'}
        </div>
        {usernameError && username.length > 0 && (
          <div className="edit-profile-error" data-testid="edit-username-error">
            {usernameError}
          </div>
        )}

        {serverError && (
          <div className="edit-profile-error" data-testid="edit-server-error">
            {serverError}
          </div>
        )}

        <div className="row" style={{ gap: 8, marginTop: 16 }}>
          <button
            type="button"
            className="btn ghost full"
            onClick={onCancel}
            disabled={saving}
            data-testid="edit-cancel"
          >
            cancel
          </button>
          <button
            type="button"
            className="btn pink full"
            onClick={onSave}
            disabled={!canSave}
            data-testid="edit-save"
          >
            {saving ? 'saving…' : 'SAVE'}
          </button>
        </div>
      </dialog>
      <style>{`
        .edit-profile-backdrop {
          position: absolute;
          inset: 0;
          z-index: 100;
          background: rgba(0,0,0,0.45);
          display: grid;
          align-items: end;
          justify-items: stretch;
          padding: 18px 16px calc(var(--safe-bottom, env(safe-area-inset-bottom)) + 100px);
        }
        .edit-profile-dismiss {
          position: absolute;
          inset: 0;
          background: transparent;
          border: none;
          padding: 0;
          margin: 0;
          cursor: pointer;
        }
        .edit-profile-sheet {
          position: relative;
          margin: 0;
          width: 100%;
          background: var(--paper);
          color: var(--ink);
          border: 3px solid var(--line);
          border-radius: 22px;
          box-shadow: 0 8px 0 var(--shadow-ink);
          padding: 18px 18px 16px;
        }
        .edit-profile-input {
          display: block;
          width: 100%;
          margin-top: 4px;
          padding: 12px 14px;
          font-size: 16px; /* prevent iOS auto-zoom */
          font-family: var(--font-mono, monospace);
          background: var(--paper);
          color: var(--ink);
          border: 2.5px solid var(--line);
          border-radius: 12px;
          box-shadow: 0 3px 0 var(--shadow-ink);
        }
        .edit-profile-input:focus {
          outline: none;
          box-shadow: 0 3px 0 var(--shadow-ink), 0 0 0 3px var(--pink);
        }
        .edit-profile-input:disabled { opacity: 0.6; }
        .edit-profile-error {
          margin-top: 6px;
          font-family: var(--font-display);
          font-size: 12px;
          color: var(--pink);
          text-transform: lowercase;
        }
      `}</style>
    </div>
  )
}
